import { useMemo } from 'react';
import { buildCompositorManifest } from '../utils/manifestBuilder';
import { CompositorManifest, CompositorRecordingData } from '../types/manifest';
import { SourceResolver } from '../utils/sourceResolver';
import { DEFAULT_FPS } from '../constants';

interface UseCompositorManifestOptions {
  recordingData: CompositorRecordingData | null;
  pendingBlobs: Map<string, Blob>;
  pendingBlobUrls?: Map<string, string>;
  segmentBlobs?: Map<string, Map<number, Blob>>;
  sourceResolver: SourceResolver;
  fps?: number;
}

export function useCompositorManifest({
  recordingData,
  pendingBlobs,
  pendingBlobUrls,
  segmentBlobs,
  sourceResolver,
  fps = DEFAULT_FPS,
}: UseCompositorManifestOptions): CompositorManifest | null {
  const pendingBlobsKey = useMemo(() => {
    return Array.from(pendingBlobs.keys()).sort().join(',');
  }, [pendingBlobs]);
  
  const pendingBlobUrlsKey = useMemo(() => {
    return pendingBlobUrls ? Array.from(pendingBlobUrls.keys()).sort().join(',') : '';
  }, [pendingBlobUrls]);

  const segmentBlobsKey = useMemo(() => {
    if (!segmentBlobs) return '';
    const keys: string[] = [];
    segmentBlobs.forEach((map, streamType) => {
      map.forEach((_, index) => {
        keys.push(`${streamType}-${index}`);
      });
    });
    return keys.sort().join(',');
  }, [segmentBlobs]);

  const manifest = useMemo(() => {
    if (!recordingData) {
      return null;
    }

    try {
      return buildCompositorManifest(recordingData, {
        fps,
        sourceResolver,
        pendingBlobs,
        pendingBlobUrls,
        segmentBlobs,
      });
    } catch (error) {
      console.error('[Compositor] Failed to build manifest:', error);
      return null;
    }
  }, [
    recordingData?.duration,
    recordingData?.layout,
    recordingData?.streams,
    pendingBlobsKey,
    pendingBlobUrlsKey,
    segmentBlobsKey,
    fps,
    sourceResolver,
  ]);

  return manifest;
}
